import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { adminService } from '../../services/adminService';
import '../../styles/admin.css';
import Fuse from 'fuse.js';

const PAGE_SIZE = 12;

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    category: '',
    description: '',
    price: '',
    stockQuantity: '',
    imageUrl: ''
  });

  // Pagination states
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Store all products for fuzzy search and client-side filtering/pagination
  const [allProducts, setAllProducts] = useState([]);

  // Fetch all products on mount AND after add/delete/edit
  const fetchAllProducts = async () => {
    try {
      const all = await adminService.getProducts(0, 10000);
      setAllProducts(all || []);
    } catch {
      setAllProducts([]);
    }
  };

  useEffect(() => {
    fetchAllProducts();
  }, []);

  useEffect(() => {
    loadProducts(page, searchTerm);
    // eslint-disable-next-line
  }, [page, searchTerm, allProducts]);

  useEffect(() => {
    if (!searchTerm) fetchTotalProducts();
    // eslint-disable-next-line
  }, [searchTerm, allProducts]);

  const fetchTotalProducts = async () => {
    try {
      setTotalProducts(allProducts.length);
    } catch (err) {
      setTotalProducts(0);
    }
  };

  const loadProducts = async (pageNum = 1, search = '') => {
    setLoading(true);
    try {
      let data = allProducts;
      // Fuzzy search by name/description/category
      if (search) {
        const fuse = new Fuse(data, {
          keys: [
            { name: 'name', weight: 0.6 },
            { name: 'description', weight: 0.25 },
            { name: 'category', weight: 0.1 },
            { name: 'categories', weight: 0.05 }
          ],
          threshold: 0.38,
          minMatchCharLength: 2,
          includeScore: false
        });
        const results = fuse.search(search);
        data = results.map(r => r.item);
      }
      const count = data.length;
      const paged = data.slice((pageNum - 1) * PAGE_SIZE, pageNum * PAGE_SIZE);
      setProducts(paged);
      setTotalProducts(count);
    } catch (err) {
      console.error('Error loading products:', err);
      toast.error('Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setProductForm({
      ...productForm,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const categories = productForm.category
      ? productForm.category.split(',').map(c => c.trim()).filter(Boolean)
      : [];
    const payload = {
      ...productForm,
      price: parseFloat(productForm.price),
      stockQuantity: parseInt(productForm.stockQuantity, 10),
      categories,
    };
    delete payload.category;

    try {
      if (editingProduct) {
        await adminService.updateProduct(editingProduct.id, payload);
        toast.success('✅ Product updated successfully!');
      } else {
        await adminService.createProduct(payload);
        toast.success('🎉 Product added to catalog!');
      }
      resetForm();
      setSearchTerm('');
      setPage(1);
      await fetchAllProducts();
    } catch (err) {
      console.error('Error saving product:', err);
      toast.error('❌ Error saving product');
    }
  };

  const handleDelete = async (productId) => {
    const product = (Array.isArray(products) ? products : []).find(p => p.id === productId);
    if (!window.confirm(`Delete "${product?.name}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await adminService.deleteProduct(productId);
      toast.success('🗑️ Product deleted successfully!');
      setSearchTerm('');
      setPage(1);
      await fetchAllProducts();
    } catch (err) {
      console.error('Error deleting product:', err);
      toast.error('❌ Delete failed');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || '',
      category: Array.isArray(product.categories)
        ? product.categories.join(', ')
        : (product.category || ''),
      description: product.description || '',
      price: product.price != null ? product.price.toString() : '',
      stockQuantity: product.stockQuantity != null ? product.stockQuantity.toString() : '',
      imageUrl: product.imageUrl || ''
    });
    setShowAddForm(true);
    setTimeout(() => {
      document.querySelector('.product-form-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const resetForm = () => {
    setProductForm({
      name: '',
      category: '',
      description: '',
      price: '',
      stockQuantity: '',
      imageUrl: ''
    });
    setEditingProduct(null);
    setShowAddForm(false);
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'MAD' }).format(price);

  const getTotalValue = () =>
    (Array.isArray(products) ? products : []).reduce((total, p) => total + (p.price * p.stockQuantity), 0);

  const totalPages = Math.max(1, Math.ceil(totalProducts / PAGE_SIZE));

  return (
    <div className="admin-products" style={{ background: "#f4f5f7", minHeight: "100vh", paddingBottom: 40 }}>
      <div className="container" style={{ maxWidth: 1150, margin: "0 auto", padding: "0 20px" }}>
        {/* Header */}
        <div className="admin-header" style={{ margin: "30px 0 18px 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div className="admin-header-content">
            <h1 style={{ fontWeight: 800, fontSize: 28, color: "#222" }}>
              <i className="fas fa-boxes" style={{ color: "#8374e6", marginRight: 9 }}></i>
              El Hiba Para Product Management
            </h1>
            <div className="breadcrumb" style={{ marginTop: 5 }}>
              <Link to="/admin/dashboard" style={{ color: "#8374e6", textDecoration: "none", fontWeight: 500 }}>
                <i className="fas fa-tachometer-alt"></i> Dashboard
              </Link>
              <span style={{ margin: "0 7px" }}>/</span>
              <span style={{ color: "#e74c3c" }}>
                <i className="fas fa-boxes"></i> Products
              </span>
            </div>
          </div>
          <div className="admin-actions">
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className={`btn ${showAddForm ? 'btn-outline' : 'btn-primary'}`}
              style={{
                background: showAddForm ? "#fff" : "#8374e6",
                color: showAddForm ? "#8374e6" : "#fff",
                border: `2px solid #8374e6`,
                borderRadius: 10,
                fontWeight: 700,
                fontSize: 17,
                padding: "9px 24px",
                boxShadow: showAddForm ? "none" : "0 2px 8px #e3e3ff"
              }}
            >
              <i className={`fas ${showAddForm ? 'fa-times' : 'fa-plus'}`} />
              {showAddForm ? 'Cancel' : 'Add New Product'}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="stats-row" style={{
          display: "flex", gap: 24, marginBottom: 30
        }}>
          <div className="stat-item" style={{
            flex: 1, background: "#fff", borderRadius: 16, padding: "22px 14px 20px 18px", boxShadow: "0 2px 16px #f4eaea", display: "flex", alignItems: "center"
          }}>
            <i className="fas fa-box" style={{ color: "#8374e6", fontSize: 30, marginRight: 16 }}></i>
            <div>
              <span className="stat-number" style={{ fontSize: 25, fontWeight: 700 }}>{totalProducts}</span>
              <span className="stat-label" style={{ color: "#7a8299", fontWeight: 500, fontSize: 15, display: "block" }}>Total Products</span>
            </div>
          </div>
          <div className="stat-item" style={{
            flex: 1, background: "#fff", borderRadius: 16, padding: "22px 14px 20px 18px", boxShadow: "0 2px 16px #f4eaea", display: "flex", alignItems: "center"
          }}>
            <i className="fas fa-warehouse" style={{ color: "#f9ca24", fontSize: 30, marginRight: 16 }}></i>
            <div>
              <span className="stat-number" style={{ fontSize: 25, fontWeight: 700 }}>
                {products.reduce((sum, p) => sum + p.stockQuantity, 0)}
              </span>
              <span className="stat-label" style={{ color: "#7a8299", fontWeight: 500, fontSize: 15, display: "block" }}>Total Stock</span>
            </div>
          </div>
          <div className="stat-item" style={{
            flex: 1, background: "#fff", borderRadius: 16, padding: "22px 14px 20px 18px", boxShadow: "0 2px 16px #f4eaea", display: "flex", alignItems: "center"
          }}>
            <i className="fas fa-dollar-sign" style={{ color: "#e74c3c", fontSize: 30, marginRight: 16 }}></i>
            <div>
              <span className="stat-number" style={{ fontSize: 25, fontWeight: 700 }}>{formatPrice(getTotalValue())}</span>
              <span className="stat-label" style={{ color: "#7a8299", fontWeight: 500, fontSize: 15, display: "block" }}>Inventory Value</span>
            </div>
          </div>
        </div>

        {/* SEARCH BAR */}
        <div className="search-section" style={{ marginBottom: 14 }}>
          <div className="search-container" style={{ display: "flex", alignItems: "center", gap: 13 }}>
            <div className="search-input-wrapper" style={{ position: "relative", flex: 1 }}>
              <i className="fas fa-search search-icon" style={{
                position: "absolute", left: 10, top: 10, color: "#8374e6", fontSize: 18
              }}></i>
              <input
                type="text"
                className="search-input"
                placeholder="Recherchez un produit par nom, description ou catégorie..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                style={{
                  width: "100%", fontSize: 16, border: "1.5px solid #e5e5e5", borderRadius: 8, padding: "8px 14px 8px 36px"
                }}
              />
              {searchTerm && (
                <button
                  className="clear-search-btn"
                  onClick={() => {
                    setSearchTerm('');
                    setPage(1);
                  }}
                  title="Effacer la recherche"
                  style={{
                    position: "absolute", right: 8, top: 7, background: "none", border: "none", color: "#e74c3c", fontSize: 18, cursor: "pointer"
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
            <div className="search-info" style={{ fontSize: 15, color: "#8b8fa7" }}>
              {searchTerm
                ? <p style={{ margin: 0 }}>
                  {totalProducts} résultat{totalProducts !== 1 ? 's' : ''} trouvé{totalProducts !== 1 ? 's' : ''}
                  {` pour "${searchTerm}"`}
                </p>
                : <p style={{ margin: 0 }}>Affichage de {totalProducts} produits</p>
              }
            </div>
          </div>
        </div>
        {/* END SEARCH BAR */}

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="product-form-section" style={{ margin: "26px 0" }}>
            <div className="form-card" style={{
              background: "#fff", borderRadius: 15, boxShadow: "0 2px 16px #f4eaea", padding: "24px 33px 22px 33px", maxWidth: 600, margin: "0 auto"
            }}>
              <h2 style={{ fontWeight: 700, fontSize: 22, color: "#8374e6", marginBottom: 19 }}>
                <i className={`fas ${editingProduct ? 'fa-edit' : 'fa-plus'}`} />
                {editingProduct ? ' Edit Product' : ' Add New Product'}
              </h2>
              <form onSubmit={handleSubmit} className="product-form">
                <div className="form-row" style={{ display: "flex", gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="name" style={{ fontWeight: 600, color: "#222" }}>
                      <i className="fas fa-tag"></i> Product Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={productForm.name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Vitamin C 1000mg"
                      style={{ width: "100%", margin: "6px 0 14px 0", padding: "7px 11px", borderRadius: 7, border: "1.2px solid #ececec", fontSize: 16 }}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="category" style={{ fontWeight: 600, color: "#222" }}>
                      <i className="fas fa-bookmark"></i> Category *
                    </label>
                    <input
                      type="text"
                      id="category"
                      name="category"
                      value={productForm.category}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Hydrating Cleanser"
                      style={{ width: "100%", margin: "6px 0 14px 0", padding: "7px 11px", borderRadius: 7, border: "1.2px solid #ececec", fontSize: 16 }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="description" style={{ fontWeight: 600, color: "#222" }}>
                    <i className="fas fa-align-left"></i> Product Description *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={productForm.description}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    placeholder="Detailed product description..."
                    style={{ width: "100%", margin: "6px 0 14px 0", padding: "9px 11px", borderRadius: 7, border: "1.2px solid #ececec", fontSize: 15 }}
                  />
                </div>
                <div className="form-row" style={{ display: "flex", gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="price" style={{ fontWeight: 600, color: "#222" }}>
                      <i className="fas fa-dollar-sign"></i> Price (MAD) *
                    </label>
                    <input
                      type="number"
                      id="price"
                      name="price"
                      value={productForm.price}
                      onChange={handleInputChange}
                      required
                      step="0.01"
                      min="0"
                      placeholder="29.99"
                      style={{ width: "100%", margin: "6px 0 14px 0", padding: "7px 11px", borderRadius: 7, border: "1.2px solid #ececec", fontSize: 16 }}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="stockQuantity" style={{ fontWeight: 600, color: "#222" }}>
                      <i className="fas fa-warehouse"></i> Stock Quantity *
                    </label>
                    <input
                      type="number"
                      id="stockQuantity"
                      name="stockQuantity"
                      value={productForm.stockQuantity}
                      onChange={handleInputChange}
                      required
                      min="0"
                      placeholder="100"
                      style={{ width: "100%", margin: "6px 0 14px 0", padding: "7px 11px", borderRadius: 7, border: "1.2px solid #ececec", fontSize: 16 }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="imageUrl" style={{ fontWeight: 600, color: "#222" }}>
                    <i className="fas fa-image"></i> Product Image URL
                  </label>
                  <input
                    type="url"
                    id="imageUrl"
                    name="imageUrl"
                    value={productForm.imageUrl}
                    onChange={handleInputChange}
                    placeholder="https://images.unsplash.com/photo-..."
                    style={{ width: "100%", margin: "6px 0 14px 0", padding: "7px 11px", borderRadius: 7, border: "1.2px solid #ececec", fontSize: 16 }}
                  />
                  <small style={{ color: "#8374e6" }}>💡 Use Unsplash for high-quality product images</small>
                </div>
                <div className="form-actions" style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                  <button type="button" onClick={resetForm} className="btn btn-outline"
                    style={{ padding: "7px 19px", borderRadius: 8, border: "1.3px solid #e0e0e0", background: "#fff", color: "#e74c3c", fontWeight: 600 }}>
                    <i className="fas fa-times" /> Cancel
                  </button>
                  <button type="submit" className="btn btn-primary"
                    style={{ background: "#8374e6", color: "#fff", border: "none", padding: "8px 20px", fontWeight: 600, borderRadius: 8 }}>
                    <i className="fas fa-save" />
                    {editingProduct ? 'Update Product' : 'Add to Catalog'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Products Grid */}
        <div className="products-section">
          <div className="section-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "22px 0 14px 0" }}>
            <h2 style={{ fontWeight: 700, fontSize: 22, color: "#222" }}>
              <i className="fas fa-list"></i> El Hiba Para Product Catalog
            </h2>
            <span className="product-count" style={{ color: "#8374e6", fontWeight: 600, fontSize: 18 }}>{totalProducts} products</span>
          </div>
          {/* Pagination Controls (top) */}
          <div style={{ textAlign: 'center', margin: '18px 0 0 0' }}>
            <button
              className="btn btn-primary"
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
              disabled={page === 1}
              style={{ marginRight: '10px', background: "#8374e6", border: "none" }}
            >
              Précédent
            </button>
            <span style={{ fontWeight: 600, color: "#8374e6" }}>Page {page} sur {totalPages}</span>
            <button
              className="btn btn-primary"
              onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
              disabled={page === totalPages}
              style={{ marginLeft: '10px', background: "#8374e6", border: "none" }}
            >
              Suivant
            </button>
          </div>
          {loading ? (
            <div className="loading-container" style={{ textAlign: "center", marginTop: 60 }}>
              <div className="loading-spinner" style={{ margin: "0 auto 15px auto" }}>
                <div className="spinner" />
                <p>Loading products…</p>
              </div>
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="products-grid" style={{
                display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 28, marginTop: 18
              }}>
                {products.map(product => (
                  <div key={product.id} className="product-card" style={{
                    background: "#fff", borderRadius: 19, boxShadow: "0 2px 16px #f4eaea",
                    padding: "21px 13px 15px 13px", minHeight: 270, display: "flex", flexDirection: "column", alignItems: "center", position: "relative"
                  }}>
                    <div className="product-image" style={{ width: "100%", display: "flex", justifyContent: "center", marginBottom: 12 }}>
                      <img
                        src={product.imageUrl || 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=300&h=300&fit=crop'}
                        alt={product.name}
                        style={{ maxWidth: 140, maxHeight: 110, borderRadius: 10, objectFit: "contain", background: "#fafafa", border: "1px solid #f2f2f2", boxShadow: "0 1px 5px #f7eaea" }}
                        onError={e => { e.target.src = 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=300&h=300&fit=crop'; }}
                      />
                      <div className="product-overlay" style={{
                        position: "absolute", top: 12, right: 16, display: "flex", gap: 6
                      }}>
                        <button onClick={() => handleEdit(product)} className="btn btn-sm btn-primary"
                          style={{ background: "#8374e6", border: "none", color: "#fff", borderRadius: 7, padding: "5px 11px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>
                          <i className="fas fa-edit" />
                        </button>
                        <button onClick={() => handleDelete(product.id)} className="btn btn-sm btn-danger"
                          style={{ background: "#e74c3c", border: "none", color: "#fff", borderRadius: 7, padding: "5px 11px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>
                          <i className="fas fa-trash" />
                        </button>
                      </div>
                    </div>
                    <div className="product-info" style={{ width: "100%" }}>
                      <h3 style={{ fontWeight: 700, fontSize: 19, color: "#8374e6", marginBottom: 3 }}>{product.name}</h3>
                      <p className="category" style={{ color: "#8b8fa7", fontWeight: 500, fontSize: 15, marginBottom: 5 }}>
                        {Array.isArray(product.categories)
                          ? product.categories.join(', ')
                          : (product.category || '')}
                      </p>
                      <p className="description" style={{ color: "#444", fontSize: 15, marginBottom: 10 }}>
                        {product.description}
                      </p>
                      <div className="product-meta" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                        <span className="price" style={{ fontWeight: 700, color: "#e74c3c", fontSize: 17 }}>{formatPrice(product.price)}</span>
                        <span className={`stock ${product.stockQuantity > 0 ? 'in-stock' : 'out-of-stock'}`} style={{ color: product.stockQuantity > 0 ? "#5dc25a" : "#e74c3c", fontWeight: 600 }}>
                          <i className={`fas ${product.stockQuantity > 0 ? 'fa-check-circle' : 'fa-exclamation-circle'}`} />
                          {product.stockQuantity > 0 ? ` ${product.stockQuantity} in stock` : 'Out of stock'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Pagination Controls (bottom) */}
              <div style={{ textAlign: 'center', margin: '30px 0' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => setPage(prev => Math.max(1, prev - 1))}
                  disabled={page === 1}
                  style={{ marginRight: '10px', background: "#8374e6", border: "none" }}
                >
                  Précédent
                </button>
                <span style={{ fontWeight: 600, color: "#8374e6" }}>Page {page} sur {totalPages}</span>
                <button
                  className="btn btn-primary"
                  onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={page === totalPages}
                  style={{ marginLeft: '10px', background: "#8374e6", border: "none" }}
                >
                  Suivant
                </button>
              </div>
            </>
          ) : (
            <div className="empty-state" style={{ textAlign: "center", marginTop: 55 }}>
              <i className="fas fa-box-open" style={{ fontSize: 38, color: "#8374e6" }}></i>
              <h3 style={{ fontWeight: 700, color: "#222", margin: "18px 0 5px 0" }}>No Products Yet</h3>
              <p style={{ color: "#7a8299", fontSize: 16 }}>Start building your catalog by adding your first product!</p>
              <button onClick={() => setShowAddForm(true)}
                className="btn btn-primary btn-large"
                style={{ background: "#8374e6", color: "#fff", border: "none", padding: "10px 27px", borderRadius: 9, fontWeight: 700, fontSize: 17, marginTop: 15 }}>
                <i className="fas fa-plus" /> Add Your First Product
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;